import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { FaDownload, FaExpand, FaTimes } from "react-icons/fa";
import { Navbar, Header, Footer, Button } from "../components";
import ErrorPage from "./ErrorPage";
import customAxios from "../utils/customAxios";

import { styles } from "../styles";

const InformasiAkomodasiPage = () => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  // 1. Add state to track which image is currently being viewed in fullscreen
  const [fullscreenImage, setFullscreenImage] = useState(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["accomodationInformations"],

    queryFn: async () => {
      const response = await customAxios.get("accommodation-informations");

      return response?.data?.data || null;
    },
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });

  const handleDownload = (imageUrl) => {
    const backendApiUrl = `${process.env.REACT_APP_CUSTOM_API_BASE_URL}/download`;
    const fullDownloadUrl = `${backendApiUrl}?url=${encodeURIComponent(imageUrl)}`;

    const link = document.createElement("a");
    link.href = fullDownloadUrl;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (isError) {
    return (
      <ErrorPage
        code="500"
        titleKey="error.500_title"
        defaultTitle="Gagal Memuat Data"
        subtitleKey="error.500_subtitle"
        defaultSubtitle="Terjadi masalah saat terhubung ke server kami. Silakan coba lagi nanti atau kembali ke"
      />
    );
  }

  const renderContent = () => {
    if (isLoading)
      return (
        <div className="w-full space-y-8 animate-pulse">
          <div className="w-full p-20 bg-gray-200 rounded"></div>
          <div className="w-full p-20 bg-gray-200 rounded"></div>
          <div className="w-full p-20 bg-gray-200 rounded"></div>
        </div>
      );
    if (!data) return null;

    return (
      <>
        {data.map((info, index) => (
          <div
            className="flex smd:flex-col w-full max-h-full gap-8 px-6 py-6 bg-[#EFEFEF] rounded-[8px] items-center justify-start"
            key={index}
          >
            {/* 2. Make the container relative so the button can be positioned inside it */}
            <div className="flex relative w-[120px] overflow-hidden rounded-md group shrink-0">
              <img
                src={info.image}
                alt={currentLanguage === "en" ? info.title_en : info.title_id}
                className="object-cover w-full h-full"
              />

              {/* 3. Fullscreen Button (appears on hover) */}
              <button
                onClick={() => setFullscreenImage(info.image)}
                className="absolute top-0 right-0 p-1.5 text-white transition-all bg-black bg-opacity-50 rounded-bl-md hover:bg-opacity-80"
                aria-label="View fullscreen"
              >
                <FaExpand size={14} />
              </button>
            </div>
            <div className="flex flex-col gap-2">
              <h1 className={`${styles.headingM} font-bold smd:text-center`}>
                {currentLanguage === "en" ? info.title_en : info.title_id}
              </h1>
              <p className={`${styles.textLMediumM} smd:text-center`}>
                {currentLanguage === "en"
                  ? info.short_description_en
                  : info.short_description_id}
              </p>
              <div className="mt-6 smd:self-center">
                <Button
                  title="Download Brosur"
                  icon={<FaDownload />}
                  bg="bg-primary-40"
                  text="text-warning-40"
                  customization="text-warning-40 border-solid border border-[#920E34] gap-4 hover:text-white hover:bg-[#920E34] transition-all"
                  type="submit"
                  onClick={(evt) => {
                    evt.preventDefault();
                    handleDownload(info.image);
                  }}
                />
              </div>
            </div>
          </div>
        ))}
      </>
    );
  };

  return (
    <>
      <Navbar />
      <Header title={t("accomodation.header")} />
      <main
        className={`${styles.paddingX} flex flex-col gap-6 py-20 min-h-[600px]`}
      >
        {renderContent()}
      </main>
      <Footer />

      {/* 4. Fullscreen Overlay Modal */}
      {fullscreenImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-80 backdrop-blur-sm"
          onClick={() => setFullscreenImage(null)} // Closes when clicking the dark background
        >
          <div className="relative max-w-full max-h-full">
            <img
              src={fullscreenImage}
              alt="Fullscreen view"
              className="object-contain max-w-full max-h-[90vh] rounded-md shadow-2xl"
              onClick={(e) => e.stopPropagation()} // Prevents closing when clicking the image itself
            />
            {/* Optional Close Button */}
            <button
              onClick={() => setFullscreenImage(null)}
              className="absolute p-2 text-white transition-colors bg-black bg-opacity-50 rounded-full -top-4 -right-4 hover:bg-opacity-80"
            >
              <FaTimes size={20} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default InformasiAkomodasiPage;
